<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';

import { ref } from 'vue';

import { useAlert } from '../composables/useAlert';
import { session, supabase } from '../supabase';

const alert = useAlert();

const dialog = ref(false);
const isBusy = ref(false);
const password = ref('');
const confirmPassword = ref('');

const rules = {
  passwordRequirements: (value: string) => {
    if (value.length < 8) {
      return 'Password must be at least 8 characters';
    }
    return true;
  },
  confirmationRequired: (value: string) => {
    if (value !== password.value) {
      return 'Password does not match';
    }
    return true;
  },
};

async function savePassword(event: SubmitEventPromise) {
  // wait for validation to complete
  const result = await event;
  if (!result.valid) return;

  isBusy.value = true;
  try {
    const { error } = await supabase.auth.updateUser({
      password: password.value,
    });
    if (error) throw error;
  }
  catch (error) {
    if (error instanceof Error) {
      alert.error(error.message);
    }
    else {
      console.error(error);
    }
    return;
  }
  finally {
    isBusy.value = false;
  }
  // Close the dialog
  dialog.value = false;
}

function cancel() {
  // Reset password fields
  password.value = '';
  confirmPassword.value = '';

  // Close the dialog
  dialog.value = false;
}

function open() {
  dialog.value = true;
}

defineExpose({ open });
</script>

<template>
  <v-dialog v-model="dialog" max-width="500px">
    <v-form :disabled="isBusy" @submit.prevent="savePassword">
      <v-card :loading="isBusy" :disabled="isBusy">
        <template #loader="{ isActive }">
          <v-progress-linear
            :active="isActive"
            color="primary"
            height="4"
            indeterminate
          />
        </template>
        <input
          id="username"
          aria-label="User name"
          type="email"
          name="username"
          autocomplete="username"
          :value="session?.user.email"
          style="display: none"
        >
        <v-card-title class="text-headline-small">
          Set Password
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="password"
            label="Password"
            type="password"
            autocomplete="new-password"
            :rules="[rules.passwordRequirements]"
          />
          <v-text-field
            v-model="confirmPassword"
            label="Confirm Password"
            type="password"
            autocompete="new-password"
            :rules="[rules.confirmationRequired]"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn type="submit" color="primary">
            Save
          </v-btn>
          <v-btn color="secondary" @click="cancel">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>
